import { createGlobalStyle } from 'styled-components'

// Global styles inspired by Vulcan
export const GlobalStyles = createGlobalStyle`
  * {
    box-sizing: border-box;
    font-family: ${({ theme }) => theme.typography.base.family};
    font-smoothing: antialiased;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  html {
    font-size: ${({ theme }) => theme.typography.base.fontSize};
    line-height: ${({ theme }) => theme.typography.base.lineHeight};
  }

  body {
    margin: 0;
    padding: 0;
    background-color: ${({ theme }) => theme.palette.background.muted};
    color: ${({ theme }) => theme.palette.text.default};
    font-family: ${({ theme }) => theme.typography.body.family};
    font-size: ${({ theme }) => theme.typography.body.size.md};
    line-height: ${({ theme }) => theme.typography.body.lineHeight.base};
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: ${({ theme }) => theme.typography.heading.family};
    font-weight: ${({ theme }) => theme.typography.weight.semibold};
    line-height: ${({ theme }) => theme.typography.heading.lineHeight.short};
    margin: 0 0 ${({ theme }) => theme.spacing[40]} 0;
    color: ${({ theme }) => theme.palette.text.default};
  }

  h1 {
    font-size: ${({ theme }) => theme.typography.heading.size.xl};
  }

  h2 {
    font-size: ${({ theme }) => theme.typography.heading.size.lg};
  }

  h3 {
    font-size: ${({ theme }) => theme.typography.heading.size.md};
  }

  h4 {
    font-size: ${({ theme }) => theme.typography.heading.size.sm};
  }

  h5, h6 {
    font-size: ${({ theme }) => theme.typography.heading.size.xs};
  }

  p {
    margin: 0 0 ${({ theme }) => theme.spacing[40]} 0;
    font-size: ${({ theme }) => theme.typography.body.size.md};
    line-height: ${({ theme }) => theme.typography.body.lineHeight.base};
  }

  a {
    color: ${({ theme }) => theme.palette.link.default};
    text-decoration: underline;
    text-underline-offset: 0.2em;
    cursor: pointer;
    transition: color 0.2s ease-in-out;

    &:hover {
      color: ${({ theme }) => theme.palette.link.hover};
    }

    &:active {
      color: ${({ theme }) => theme.palette.link.active};
    }

    &:visited {
      color: ${({ theme }) => theme.palette.link.visited};
    }
  }

  button {
    font-family: inherit;
    cursor: pointer;
    border: none;
    background: none;
    padding: 0;
    margin: 0;

    &:focus {
      outline: ${({ theme }) => theme.component.interaction.focus.outline};
      outline-offset: ${({ theme }) => theme.component.interaction.focus.outlineOffset};
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  input, textarea, select {
    font-family: inherit;
    font-size: inherit;
    border: none;
    background: none;
    padding: 0;
    margin: 0;

    &:focus {
      outline: none;
    }
  }

  ul, ol {
    margin: 0 0 ${({ theme }) => theme.spacing[40]} 0;
    padding-left: ${({ theme }) => theme.spacing[60]};
  }

  li {
    margin-bottom: ${({ theme }) => theme.spacing[20]};
  }

  /* Remove default styles */
  * {
    &::before,
    &::after {
      box-sizing: border-box;
    }
  }

  /* Focus styles for accessibility */
  *:focus {
    outline: ${({ theme }) => theme.component.interaction.focus.outline};
    outline-offset: ${({ theme }) => theme.component.interaction.focus.outlineOffset};
  }

  /* High contrast mode support */
  @media (prefers-contrast: high) {
    * {
      border-color: currentColor !important;
    }
  }

  /* Reduced motion support */
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }
`

// Utility function to get theme tokens
export const token = (path: string, themeObj?: any): string => {
  const keys = path.split('.')
  let value: any = themeObj
  
  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key]
    } else {
      console.warn(`Token path "${path}" not found in theme`)
      return ''
    }
  }
  
  return typeof value === 'string' || typeof value === 'number' ? String(value) : ''
}